<template>
  <div>
    <breadcrumbs :home="vessel.name + ': Move contents to tank'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row dense>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
            <v-col class="text-caption ml-5 mt-3 grey--text">{{vesselContents}}</v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" sm="3" lg="2">
              <alcohol-input label="Volume" :spirit="spirit || !vessel.contents.volume"
                            :tare="vessel.tare" v-model="transaction.volume" :litres.sync="transaction.litres" :disabled="!edit"
                            :kg.sync="transaction.kg" :strength.sync="transaction.strength"
                            :hint="vesselVolume"/>
            </v-col>
            <v-col>
              <v-btn x-small text class="mt-3" @click="allContents()">
                Use all contents
              </v-btn>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" lg="4">
              <tank-select v-model="transaction.destinationTankId" :tanks="tanks" label="Move contents to tank" outlined :hint="transactionId===undefined"
                          :highlight="tank => transactionId === undefined && tank.contents.alcoholId === vessel.contents.alcoholId"/>
            </v-col>
          </v-row>
          <v-row dense v-if="allowReduce">
            <v-col cols="12" sm="6" lg="4">
              <v-checkbox label="Reduce remaining inventory to zero" class="mt-1" v-model="transaction.reduceToZero"/>
            </v-col>
          </v-row>
          <v-row dense >
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'

export default {
  name: 'ExciseVesselTransferToTank',
  components: {TankSelect},
  mixins: [confirmLeave],
	props: {
    vesselId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      vessel: {name:'',contents:{}},
      valid:true,
      saving:false,
      tanks: [],
      transaction: {vesselId:this.vesselId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    volume: 0, litres: 0, kg: 0, strength: 0, comments:'', destinationTankId:'',reduceToZero:false},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    spirit() {
      return this.vessel.contents?.alcoholType === 'Spirit';
    },
    vesselContents() {
      return this.vessel.contents.volume ? this.vessel.contents.alcoholName : '';
    },
    vesselVolume() {
      if (!this.transactionId)
        return this.vessel.contents.volume ? `${this.$number(this.vessel.contents.volume, 3)} ${this.spirit?'LAA':'litres'}` : '(empty)';
    },
    allowReduce() {
      return this.vessel.vesselType === 'Cask';
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    if (this.transactionId) {
      const results = await this.$apiCall(`query ExciseVesselTransferToTank($id:ID!) {
                                              exciseVesselTransferToTank(id:$id) {
                                                id,vesselId,date,volume,litres,kg,strength,comments,destinationTankId,reduceToZero
                                              }
                                            }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.exciseVesselTransferToTank;
    }
    else {
      this.$nextTick(() => this.$refs.form.validate());
      this.edit = true;
    }

    await this.refresh();
//    if (!this.transactionId && this.allowReduce)
//      this.transaction.reduceToZero = true;
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    allContents() {
      this.transaction.volume = this.vessel.contents.volume;
      this.transaction.litres = this.vessel.contents.litres;
      this.transaction.kg = this.vessel.contents.kg;
      this.transaction.strength = this.vessel.contents.strength;
    },
    async refresh() {
      let results = await this.$apiCall(`query ExciseVesselTransferToTank($id:ID!,$asof:DateTime!) {
                                            exciseVessel(id:$id) {
                                              id,vesselType,name,tare,
                                              contents(date:$asof) {alcoholType,alcoholName,alcoholId,volume,litres,siteId,strength,kg}
                                            }
                                          }`,
                                        {id:this.transaction ? this.transaction.vesselId : this.vesselId, asof:this.transaction.date});
      if (!results.error) {
        this.vessel = results.data.exciseVessel;
        this.tanks = [];
        if (this.vessel.contents.siteId) {  // Can happen if tank didn't exist yet
          await this.$lazyListLoad(this.tanks, 'tanks2', `id,name,tare,site{id},contents{volume,alcoholType,alcoholName,alcoholId}`, 100, [{field:'active', comparator:'eq', value:'true'},{field:'siteId', comparator:'eq', value:this.vessel.contents.siteId}], [{name:'asof', value:this.transaction.date}]);
          const thisTankIndex = this.tanks.findIndex(tank => tank.id === this.transaction.vesselId);
          if (thisTankIndex !== -1)
            this.tanks.splice(thisTankIndex, 1);
        }
        if (!this.transactionId) {
          if (!this.tanks.find(t => t.id === this.transaction.destinationTankId))
            this.transaction.destinationTankId = '';
          this.transaction.strength = this.vessel.contents.strength;
        }
      }
      this.$nextTick(() => {
        this.$refs.form.validate();
      })
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.transaction.value = this.calculateValue ? null : this.value;
        this.saving = true;
        const results = await this.$apiCall(`mutation ExciseVesselTransferToTank($input: ExciseVesselTransferToTankInput!) {
                                                exciseVesselTransferToTankCreate(input:$input) {id}
                                              }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} move contents to tank transaction for ${this.vessel.name}`});
        }
      }
      else
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
		}
	}
}
</script>